import React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import {
  CreatePerformanceReviewProcessFragment,
  CreateReviewFromTemplateDocument,
  PerformanceDtosReviewProcessCreateFromTemplateTemplateTypeEnum,
  ReviewProcessCustomTemplatesDocument,
  ReviewProcessPredefinedTemplatesDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { usePaginatedQuery } from 'modules/core/hooks/usePaginatedQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'
import app from 'modules/core/store/models/app'

import SimpleChip from 'modules/performance_process/components/overview/Targets/SimpleChip'
import {
  scoreConfigurationToEvent,
  targetStrategyToEvent,
} from 'modules/performance_process/lib/events'

import { TemplateListContent } from './TemplateListContent'

type TemplateFilterState = {
  templateType: PerformanceDtosReviewProcessCreateFromTemplateTemplateTypeEnum
  filterByAuthor: boolean
}

const PredefinedTemplatesList: React.FC<{
  templateId?: number
  setTemplateId: (id: number | undefined) => void
}> = ({ templateId, setTemplateId }) => {
  const {
    nodes: predefinedTemplates,
    controls,
    loading,
  } = usePaginatedQuery(ReviewProcessPredefinedTemplatesDocument, {
    extractConnection: (data) => data.performance?.reviewProcessTemplatesConnection,
  })

  return (
    <TemplateListContent
      loading={loading}
      templates={predefinedTemplates}
      templateId={templateId}
      setTemplateId={setTemplateId}
      hasMore={controls.hasMore}
      fetchMore={controls.fetchMore}
    />
  )
}

const CustomTemplatesList: React.FC<{
  templateId?: number
  setTemplateId: (id: number | undefined) => void
  filterByAuthor?: boolean
}> = ({ templateId, setTemplateId, filterByAuthor = false }) => {
  const { access } = useCurrentSession()
  const variables = filterByAuthor ? { authorIds: [access.id] } : undefined

  const {
    nodes: customTemplates,
    controls,
    loading,
  } = usePaginatedQuery(ReviewProcessCustomTemplatesDocument, {
    variables,
    fetchPolicy: 'network-only',
    extractConnection: (data) => data.performance?.reviewProcessCustomTemplatesConnection,
  })

  return (
    <TemplateListContent
      loading={loading}
      templates={customTemplates}
      templateId={templateId}
      setTemplateId={setTemplateId}
      hasMore={controls.hasMore}
      fetchMore={controls.fetchMore}
    />
  )
}

const TemplatesListStep: React.FC<{
  templateId?: number
  setTemplateId: (id: number | undefined) => void
  filterState: TemplateFilterState
  setFilterState: (state: TemplateFilterState) => void
}> = ({ templateId, setTemplateId, filterState, setFilterState }) => {
  const handleSelectTemplateType = (
    templateType: PerformanceDtosReviewProcessCreateFromTemplateTemplateTypeEnum,
    filterByAuthor = false
  ) => {
    setTemplateId(undefined)
    setFilterState({
      templateType,
      filterByAuthor,
    })
  }

  return (
    <Box gap="s32" overflowY="auto" height="s400" width="sidepanelWideXL">
      <Box flexDirection="row" gap="s8" alignItems="center">
        <SimpleChip
          label={i18n.t('performance_process.reviews.templates.predefined')}
          active={filterState.templateType === 'predefined'}
          disabled={false}
          onClick={() => handleSelectTemplateType('predefined')}
        />
        <SimpleChip
          label={i18n.t('performance_process.reviews.templates.custom')}
          active={filterState.templateType === 'custom' && !filterState.filterByAuthor}
          disabled={false}
          onClick={() => handleSelectTemplateType('custom', false)}
        />
        <SimpleChip
          label={i18n.t('performance_process.reviews.templates.my_templates')}
          active={filterState.templateType === 'custom' && filterState.filterByAuthor}
          disabled={false}
          onClick={() => handleSelectTemplateType('custom', true)}
        />
      </Box>

      {filterState.templateType === 'predefined' ? (
        <PredefinedTemplatesList templateId={templateId} setTemplateId={setTemplateId} />
      ) : (
        <CustomTemplatesList
          templateId={templateId}
          setTemplateId={setTemplateId}
          filterByAuthor={filterState.filterByAuthor}
        />
      )}
    </Box>
  )
}

interface Props {
  onClose?: () => void
  onSubmit?: (data: CreatePerformanceReviewProcessFragment) => void
  authorAccessId: number
}

export const CreateReviewFromTemplateModal: React.FC<Props> = ({
  authorAccessId,
  onSubmit,
  onClose,
}) => {
  const [submitting, setSubmitting] = React.useState(false)
  const [templateId, setTemplateId] = React.useState<number | undefined>()

  const [filterState, setFilterState] = useLocalStorage<TemplateFilterState>(
    LOCAL_STORAGE_KEYS.preSelectedTemplateType,
    {
      templateType: 'predefined',
      filterByAuthor: false,
    }
  )

  const [createReviewFromTemplate] = useMutation(CreateReviewFromTemplateDocument, {
    extractorFn: (data) => data.performanceMutations.createFromTemplatePerformanceReviewProcess,
  })

  const handleSubmit = async () => {
    if (!templateId || !filterState || submitting) return

    setSubmitting(true)

    const result = await createReviewFromTemplate({
      variables: {
        authorAccessId,
        templateId,
        templateType: filterState.templateType,
      },
    })

    const handled = handleResult(result, (errors) => {
      const messages = errors.messages
      if (messages) {
        app.setFlash({
          type: 'wrong',
          message: messages ?? i18n.t('common.error'),
        })
      }
    })

    setSubmitting(false)
    if (!handled.success) return

    const reviewProcess = handled.data?.reviewProcess
    if (!reviewProcess) return

    track(trackingEvents.performanceProcess.createReviewSubmitted, {
      review_type: 'template',
      reviewer_strategies: reviewProcess.reviewerStrategies,
      reviewees: targetStrategyToEvent(reviewProcess.targetStrategy),
      performance_score: scoreConfigurationToEvent(reviewProcess.scoreConfiguration),
    })
    onSubmit?.(reviewProcess)
  }

  if (!open) return null

  return (
    <Modal
      illustration="window-user"
      title={i18n.t('performance_process.reviews.create_from_template.title')}
      onClose={onClose}
      onSubmit={handleSubmit}
      footer={
        <Button submit disabled={!templateId || submitting} type="primary">
          {i18n.t('performance_process.reviews.create.create_review_button')}
        </Button>
      }
    >
      {filterState?.templateType && (
        <TemplatesListStep
          templateId={templateId}
          setTemplateId={setTemplateId}
          filterState={filterState}
          setFilterState={setFilterState}
        />
      )}
    </Modal>
  )
}
